import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { createResourceLocatorString } from '../../../util/routes';

import css from './MainPanelHeader.module.css';
import TopbarSearchForm from '../../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
    config,
    currentSearchParams,
    history,
    location,
    routeConfiguration,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const handleSubmit = values => {
    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        // If keywords is empty/cleared, return it as undefined so it gets
        // stripped from the URL (omit from query string entirely)
        const keywords = values?.keywords || undefined;
        return { keywords };
      }

      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };

    const newSearchTypeParams = topbarSearchParams();

    // Remove any existing keywords/location params from currentSearchParams
    // before merging, so clearing the field actually removes them from the URL.
    const {
      keywords: _kw,
      address: _addr,
      bounds: _bounds,
      origin: _origin,
      ...restCurrentParams
    } = currentSearchParams || {};

    const searchParams = {
      ...restCurrentParams,
      ...newSearchTypeParams,
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        <TopbarSearchForm appConfig={config} onSubmit={handleSubmit} />

        <h1 className={css.searchResultSummary}>
          <span className={css.resultsFound}>
            {searchInProgress ? (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            ) : (
              <FormattedMessage
                id="MainPanelHeader.foundResults"
                values={{ count: resultsCount }}
              />
            )}
          </span>
        </h1>
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>

      {children}

      {/* {noResultsInfo ? noResultsInfo : null} */}
    </div>
  );
};

export default MainPanelHeader;
